/**
 * M3X.WORKSPACE-CLOSURE A1+A2+A3+A4 — the real complete WorkspaceShell.
 *
 * The original F.14 ("big-bang migration") cert claimed this file was
 * "substantially extended" — but the Write silently failed and the
 * file stayed at the F.1 minimal skeleton (Rail 1 + Outlet only). This
 * is the actual extension that should have shipped in F.14.
 *
 * Per BUILD_READINESS_M3X_WORKSPACE_FULL.md §2.1. Layout:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Topbar (Solvait wordmark + Breadcrumb + Action cluster)       │
 *   ├────┬─────┬──────────────────────────────────────┬─────────────┤
 *   │ R1 │ R2  │ Tab strip (screens within active SD) │ AI brief    │
 *   │ 72 │ 256 ├──────────────────────────────────────┤ panel 280px │
 *   │ px │  /  │  <Outlet />                          │ (when slot   │
 *   │    │ 44  │                                      │  populated)  │
 *   │    │  /  │                                      │             │
 *   │    │  0  │                                      │             │
 *   └────┴─────┴──────────────────────────────────────┴─────────────┘
 *
 * Chat panel (M22-M25): mounted as a sibling at document level via
 * `<ChatPanel>` — slides in from the right when ⌘/ pressed or the
 * sparkle button in the topbar is clicked. The chat button event +
 * keyboard listener match the legacy AppShell pattern verbatim so
 * existing `CHAT_OPEN_EVENT` / `CHAT_EXPLAIN_EVENT` / `CHAT_OPEN_THREAD_EVENT`
 * dispatchers from M22-M25 just work.
 */

import { useAuth } from "@clerk/clerk-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";

import { NAVIGATION_REGISTRY } from "../navigation/registry";
import { ChatPanel } from "../components/chat/ChatPanel";
import { GlobalSearch, useGlobalSearchHotkey } from "../components/GlobalSearch";
import { TraceExplainerHost } from "../components/ui";
import {
  CHAT_EXPLAIN_EVENT,
  CHAT_OPEN_EVENT,
  CHAT_OPEN_THREAD_EVENT,
} from "../lib/chat-explain";
import { ContentTabStrip } from "./ContentTabStrip";
import { KbdHintCard } from "./KbdHintCard";
import { PinLimitToast } from "./PinLimitToast";
import { Rail1 } from "./Rail1";
import { Rail2 } from "./Rail2";
import { Rail2RestoreTab } from "./Rail2RestoreTab";
import { useAttentionSignals } from "./useAttentionSignals";
import { useModuleShortcuts } from "./useModuleShortcuts";
import { useRail2Keyboard } from "./useRail2Keyboard";
import { useWorkspace } from "./useWorkspace";
import { WorkspaceTopBar } from "./WorkspaceTopBar";


const _CHAT_OPEN_KEY = "solvait-aihr.chat-open";


function _initialChatOpen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(_CHAT_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}


export interface WorkspaceShellProps {
  /** Optional per-route AI brief slot (renders in the 280px right column). */
  aiPanelSlot?: React.ReactNode;
  /** Body override (otherwise Outlet renders). */
  children?: React.ReactNode;
}


export function WorkspaceShell({ aiPanelSlot, children }: WorkspaceShellProps) {
  const { userId } = useAuth();
  const { i18n } = useTranslation();
  const locale: "en" | "ar" = i18n.language === "ar" ? "ar" : "en";

  // PR 8 — /me collapse-to-home variant
  const location = useLocation();
  const isMeMode = location.pathname.startsWith("/me");

  // ⌘1-⌘7 module shortcuts (A4)
  useModuleShortcuts();

  // PR 9 — J / K / H / L vim-style Rail 2 navigation
  useRail2Keyboard();

  const ws = useWorkspace(userId ?? null);
  const attention = useAttentionSignals();
  // PR 4 — sub-domain counts feed Rail 2; per-screen counts feed ContentTabStrip
  const attentionBySubDomain = attention.bySubDomain;
  const attentionByScreen = attention.byScreen;

  // PR 6 — Rail 1 attention dots. A module gets a dot when any
  // sub-domain in it has a non-zero count. Rail 1's module ids use
  // the underscore convention; the registry uses hyphens. Map
  // both keys onto the result so either side can look up.
  const attentionByModule = useMemo<Record<string, boolean>>(() => {
    const result: Record<string, boolean> = {};
    // Build a sub-domain-id → module-id lookup from the registry.
    const sdToModule = new Map<string, string>();
    for (const sd of NAVIGATION_REGISTRY) {
      sdToModule.set(sd.id, sd.module);
    }
    for (const [sdId, count] of Object.entries(attentionBySubDomain)) {
      if (count <= 0) continue;
      const moduleId = sdToModule.get(sdId);
      if (!moduleId) continue;
      result[moduleId] = true;
      result[moduleId.replace(/-/g, "_")] = true;
    }
    return result;
  }, [attentionBySubDomain]);

  const moduleId = ws.currentModule ?? "mission-control";

  // ---- Chat panel state + ⌘/ shortcut + bus listeners (A4)
  const [chatOpen, setChatOpen] = useState<boolean>(_initialChatOpen);

  // M3X.WORKSPACE-CLOSURE Session 2.7 PR 1 — command palette state
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  useGlobalSearchHotkey(() => setSearchOpen(true));

  // Persist chatOpen across reloads (legacy pattern from AppShell)
  useEffect(() => {
    try {
      localStorage.setItem(_CHAT_OPEN_KEY, chatOpen ? "1" : "0");
    } catch {
      // localStorage unavailable — non-fatal
    }
  }, [chatOpen]);

  // ⌘/ toggles chat
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "/") {
        const target = e.target as HTMLElement | null;
        if (
          target &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.isContentEditable)
        ) {
          // Don't intercept ⌘/ when typing in chat input itself, etc.
          // (The chat input has its own send keybind.)
        }
        e.preventDefault();
        setChatOpen((o) => !o);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // M22-M25 event bus: opening the chat from elsewhere (Explainable,
  // open-thread links, etc.) sets chatOpen=true
  useEffect(() => {
    function openPanel() {
      setChatOpen(true);
    }
    window.addEventListener(CHAT_EXPLAIN_EVENT, openPanel);
    window.addEventListener(CHAT_OPEN_THREAD_EVENT, openPanel);
    window.addEventListener(CHAT_OPEN_EVENT, openPanel);
    return () => {
      window.removeEventListener(CHAT_EXPLAIN_EVENT, openPanel);
      window.removeEventListener(CHAT_OPEN_THREAD_EVENT, openPanel);
      window.removeEventListener(CHAT_OPEN_EVENT, openPanel);
    };
  }, []);

  // Keep <html dir> in sync with locale (M21 pattern)
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);

  function _toggleLocale() {
    i18n.changeLanguage(locale === "en" ? "ar" : "en");
  }

  // Right column gets the AI brief slot OR a placeholder if the route
  // didn't pass one — never empty per §11 criterion 11.
  const rightPanel = useMemo(() => aiPanelSlot ?? null, [aiPanelSlot]);

  return (
    <>
      <TraceExplainerHost />
      <div className="flex h-screen flex-col">
        <header className="flex-shrink-0">
          <WorkspaceTopBar
            currentModule={ws.currentModule}
            currentSubDomain={ws.currentSubDomain}
            currentScreen={ws.currentScreen}
            locale={locale}
            onLocaleToggle={_toggleLocale}
            onChatToggle={() => setChatOpen((o) => !o)}
            chatOpen={chatOpen}
            onSearchOpen={() => setSearchOpen(true)}
          />
        </header>

        <div className="flex min-h-0 flex-1">
          <Rail1
            activeModuleId={ws.currentModule ?? undefined}
            attentionByModule={attentionByModule}
            meMode={isMeMode}
          />

          <Rail2
            module={moduleId as Parameters<typeof Rail2>[0]["module"]}
            state={ws.effectiveRail2State}
            locale={locale}
            pinnedSubDomainIds={ws.pinnedSubDomains}
            onTogglePin={(id) =>
              ws.isPinned(id) ? ws.unpinSubDomain(id) : ws.pinSubDomain(id)
            }
            attentionCounts={attentionBySubDomain}
            onCycle={ws.cycleRail2}
            onExpand={() => ws.setRail2State("expanded")}
          />

          {/* M3X.WORKSPACE-CLOSURE Session 2.5 PR 4 — when Rail 2 is in
              the Hidden state (forced for Mission Control OR user-cycled),
              render a small restore tab on the content's start edge so
              mouse-only users have an affordance to re-enter the rail. */}
          {/* M3X.WORKSPACE-CLOSURE FINAL PR 7 follow-up — restore tab
              only when the user EXPLICITLY hid Rail 2 (rail2State =
              "hidden"). On Mission Control + other single-screen
              modules, effectiveRail2State is force-hidden by useWorkspace
              but rail2State stays at the user's preference (commonly
              "expanded"). In that case there's nothing to restore TO
              — clicking restore would just re-force-hide. So we gate
              the tab on the USER preference, not the effective state. */}
          <Rail2RestoreTab
            show={
              ws.rail2State === "hidden" &&
              ws.effectiveRail2State === "hidden" &&
              !isMeMode
            }
            onRestore={() => ws.setRail2State("expanded")}
            userId={userId ?? null}
          />

          {/* M3X.WORKSPACE-CLOSURE Session 2.7 PR 2 — main content lives
              inside a `position: relative` wrapper so EntityDrawer can
              `position: absolute` against IT, not the viewport. That
              keeps the drawer inside the main column even when chat
              docks alongside. */}
          <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            <ContentTabStrip
              subDomain={ws.currentSubDomain}
              locale={locale}
              attentionByScreen={attentionByScreen}
            />
            <div className="flex-1 overflow-auto p-6">
              {children ?? <Outlet />}
            </div>
          </main>

          {rightPanel && (
            <aside
              aria-label="AI brief"
              className="hidden w-[280px] flex-shrink-0 border-s border-border-default bg-surface-subtle xl:block"
            >
              {rightPanel}
            </aside>
          )}

          {/* M3X.WORKSPACE-CLOSURE Session 2.7 PR 2 — chat panel DOCKS
              as a real flex child (not position:fixed overlay). Main
              content reflows when chat opens. Drawer + chat coexist. */}
          <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} />
        </div>
      </div>

      {/* PR 5 — pin-limit toast */}
      <PinLimitToast
        show={ws.pinError === "limit_reached"}
        onDismiss={ws.clearPinError}
      />

      {/* PR 9 — first-session kbd hint card (auto-dismiss 8s, per-user) */}
      <KbdHintCard userId={userId ?? null} />

      {/* M3X.WORKSPACE-CLOSURE Session 2.7 PR 1 — ⌘K command palette */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}


export default WorkspaceShell;
